/* Referral card — "Join on Withdraw Guard and get $15. Terms apply." + link; friend name cycles through synced contacts. */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { Gift, Users, Share2, Copy, Check, X, Loader2, ShieldCheck, Plus, Trash2 } from "lucide-react";

const FALLBACK_NAMES = ["your friend", "your sister", "your roommate", "your coworker", "your cousin"];
const canPickContacts = () => typeof navigator !== "undefined" && "contacts" in navigator && "select" in navigator.contacts;

export default function ReferralCard({ compact = false }) {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("wg_ref_bubble_dismissed") === "1");
  const [expanded, setExpanded] = useState(false);
  const [idx, setIdx] = useState(0);
  const [copied, setCopied] = useState(false);
  const [msg, setMsg] = useState("");
  const [consentOpen, setConsentOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  const load = () => api.get("/referral/me").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const names = useMemo(() => {
    const c = (data?.contacts || []).map((x) => x.first_name || x.name).filter(Boolean);
    return c.length ? c : FALLBACK_NAMES;
  }, [data]);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % names.length), 2400);
    return () => clearInterval(t);
  }, [names.length]);

  if (!data || dismissed) return null;
  const friendName = names[idx % names.length];
  const dismiss = () => { localStorage.setItem("wg_ref_bubble_dismissed", "1"); setDismissed(true); };
  const shareText = `Join on Withdraw Guard and get $${data.friend_reward}. Terms apply. ${data.link}`;

  const share = async () => {
    setMsg("");
    const contact = (data.contacts || [])[idx % Math.max(1, (data.contacts || []).length)];
    if (contact?.phone) { window.location.href = `sms:${contact.phone}?&body=${encodeURIComponent(shareText)}`; return; }
    if (navigator.share) { try { await navigator.share({ title: "Withdraw Guard", text: shareText, url: data.link }); return; } catch { /* cancelled */ } }
    await copy();
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(data.link); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { setMsg(data.link); }
  };

  const pickContacts = async () => {
    setConsentOpen(false);
    if (!canPickContacts()) { setManualOpen(true); return; }
    try {
      const picked = await navigator.contacts.select(["name", "tel"], { multiple: true });
      const contacts = picked.map((p) => ({ name: (p.name || [])[0] || "", phone: (p.tel || [])[0] || null })).filter((c) => c.name);
      if (!contacts.length) return;
      const { data: res } = await api.put("/referral/contacts", { contacts, consent: true });
      setData((d) => ({ ...d, contacts: res.contacts, contacts_consent_at: res.contacts_consent_at }));
      setMsg(`${res.contacts.length} friend${res.contacts.length === 1 ? "" : "s"} ready to invite.`);
    } catch (e) { setMsg(formatErr(e.response?.data?.detail) || "Couldn't read contacts — add friends by name instead."); setManualOpen(true); }
  };

  const clearContacts = async () => {
    await api.delete("/referral/contacts"); setData((d) => ({ ...d, contacts: [], contacts_consent_at: null })); setMsg("Contacts removed.");
  };

  if (!expanded) {
    return (
      <div className="relative inline-flex items-center gap-3 rounded-full border border-gold/50 bg-gold/10 pl-4 pr-10 py-2.5 max-w-full" data-testid="referral-bubble">
        <Gift className="w-4 h-4 text-gold-deep shrink-0" />
        <button onClick={() => setExpanded(true)} className="text-sm text-ink text-left hover:underline" data-testid="referral-bubble-open">
          Invite <span className="font-serif italic" data-testid="referral-friend-name" key={friendName}>{friendName}</span> — they get {fmtMoney(data.friend_reward)}, you get {fmtMoney(data.referrer_reward)}
        </button>
        <button onClick={dismiss} aria-label="Dismiss invite friends" className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full flex items-center justify-center text-sage hover:text-ink hover:bg-white/60" data-testid="referral-bubble-close" data-dense><X className="w-3.5 h-3.5" /></button>
      </div>
    );
  }

  return (
    <section className={`relative rounded-2xl border border-gold/50 bg-gold/10 ${compact ? "p-4" : "p-5 sm:p-6"}`} data-testid="referral-card">
      <button onClick={dismiss} aria-label="Close invite friends" className="absolute right-3 top-3 h-8 w-8 rounded-full flex items-center justify-center text-sage hover:text-ink hover:bg-white/60" data-testid="referral-card-close"><X className="w-4 h-4" /></button>
      <div className="flex items-start justify-between gap-3 flex-wrap pr-8">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-gold-deep"><Gift className="w-3 h-3" /> Invite friends{data.rewards_mode === "demo" && " · demo rewards"}</div>
          <h3 className={`font-serif ${compact ? "text-lg" : "text-2xl"} mt-1`}>Level up together.</h3>
          <p className="text-xs text-moss mt-1 max-w-md">Your friend gets <strong>{fmtMoney(data.friend_reward)}</strong> when they become a member — and <strong>{fmtMoney(data.referrer_reward)}</strong> lands in your Spending for recommending them. <Link to="/terms" className="underline" data-testid="referral-terms-link">Terms apply.</Link>{data.rewards_mode === "demo" && " In Demo Mode rewards are play money."}</p>
        </div>
        <div className="text-right text-xs text-moss" data-testid="referral-stats">
          <div><span className="font-mono text-ink">{data.invited}</span> invited</div>
          <div><span className="font-mono text-forest">{fmtMoney(data.earned)}</span> earned{data.queued > 0 && <span className="text-gold"> · {fmtMoney(data.queued)} on the way</span>}</div>
        </div>
      </div>

      <button onClick={share} className="mt-4 w-full sm:w-auto inline-flex flex-col items-center sm:items-start justify-center gap-0.5 bg-gold text-white rounded-xl px-5 py-3 text-sm font-semibold hover:bg-gold-deep transition-colors shadow-sm text-left" data-testid="referral-share-btn">
        <span className="inline-flex items-center gap-2"><Share2 className="w-4 h-4" /> Join on Withdraw Guard and get ${Math.round(data.friend_reward)}. Terms apply.</span>
        <span className="text-[11px] font-normal text-white/85">Send to <span className="font-serif italic" data-testid="referral-friend-name" key={friendName}>{friendName}</span> → <span className="font-mono">{data.link.replace(/^https?:\/\//, "")}</span></span>
      </button>

      <div className="mt-3 flex items-center gap-3 flex-wrap text-xs">
        <button onClick={copy} className="inline-flex items-center gap-1 text-forest hover:underline" data-testid="referral-copy-link">
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {copied ? "Copied" : "Copy invite link"}
        </button>
        <span className="font-mono text-sage" data-testid="referral-code">{data.code}</span>
        {data.contacts?.length ? (
          <>
            <span className="text-sage inline-flex items-center gap-1" data-testid="referral-contacts-count"><Users className="w-3 h-3" /> {data.contacts.length} friends synced</span>
            <button onClick={() => setManualOpen(true)} className="text-forest hover:underline inline-flex items-center gap-1" data-testid="referral-add-more"><Plus className="w-3 h-3" /> Add</button>
            <button onClick={clearContacts} className="text-sage hover:text-clay inline-flex items-center gap-1" data-testid="referral-clear-contacts"><Trash2 className="w-3 h-3" /> Remove</button>
          </>
        ) : (
          <button onClick={() => setConsentOpen(true)} className="inline-flex items-center gap-1 text-forest font-medium hover:underline" data-testid="referral-sync-contacts">
            <Users className="w-3 h-3" /> Use my contacts
          </button>
        )}
      </div>
      {msg && <div className="mt-2 text-xs text-moss break-all" data-testid="referral-msg">{msg}</div>}

      {consentOpen && (
        <div className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
          <div className="bg-cream rounded-2xl border border-sand max-w-md w-full p-6" data-testid="referral-consent-dialog">
            <div className="flex items-center justify-between">
              <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-sage"><ShieldCheck className="w-3.5 h-3.5" /> Contacts permission</div>
              <button onClick={() => setConsentOpen(false)} className="text-moss hover:text-ink" data-testid="referral-consent-close"><X className="w-4 h-4" /></button>
            </div>
            <h4 className="font-serif text-xl mt-2">Can we use your contacts to personalise invites?</h4>
            <ul className="mt-3 text-sm text-moss space-y-1.5 list-disc pl-5">
              <li>You pick exactly which friends — we never read your whole address book.</li>
              <li>We save first names and numbers only to show who to send the invite to and open a text to them.</li>
              <li>Nothing is sent without you tapping send. Remove them any time.</li>
            </ul>
            <div className="mt-5 flex gap-2">
              <button onClick={pickContacts} className="flex-1 bg-forest text-white rounded-lg py-2.5 text-sm font-medium hover:bg-forest-deep" data-testid="referral-consent-allow">{canPickContacts() ? "Choose contacts" : "Add friends by name"}</button>
              <button onClick={() => setConsentOpen(false)} className="px-4 border border-sand rounded-lg text-sm" data-testid="referral-consent-deny">Not now</button>
            </div>
            {!canPickContacts() && <p className="mt-3 text-[11px] text-sage">Your browser can't open the contact picker — you can type a few names instead.</p>}
          </div>
        </div>
      )}

      {manualOpen && <ManualContacts existing={data.contacts || []} onClose={() => setManualOpen(false)} onSaved={(res) => { setData((d) => ({ ...d, contacts: res.contacts, contacts_consent_at: res.contacts_consent_at })); setManualOpen(false); setMsg(`${res.contacts.length} friend${res.contacts.length === 1 ? "" : "s"} ready to invite.`); }} />}
    </section>
  );
}

function ManualContacts({ existing, onClose, onSaved }) {
  const [rows, setRows] = useState(existing.length ? existing.map((c) => ({ name: c.name, phone: c.phone || "" })) : [{ name: "", phone: "" }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (i, k, v) => setRows((r) => r.map((row, j) => (j === i ? { ...row, [k]: v } : row)));
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.put("/referral/contacts", { contacts: rows.filter((r) => r.name.trim()), consent: true }); onSaved(data); }
    catch (e2) { setErr(formatErr(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
      <form onSubmit={save} className="bg-cream rounded-2xl border border-sand max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="referral-manual-dialog">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Friends to invite</div>
          <button type="button" onClick={onClose} className="text-moss hover:text-ink" data-testid="referral-manual-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="mt-4 space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex gap-2">
              <input value={r.name} onChange={(e) => set(i, "name", e.target.value)} placeholder="First name" maxLength={40} className="flex-1 min-w-0 bg-white border border-sand rounded-md px-3 py-2 text-sm" data-testid={`referral-manual-name-${i}`} />
              <input value={r.phone} onChange={(e) => set(i, "phone", e.target.value)} placeholder="Phone (optional)" inputMode="tel" maxLength={20} className="w-36 bg-white border border-sand rounded-md px-3 py-2 text-sm font-mono" data-testid={`referral-manual-phone-${i}`} />
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setRows((r) => [...r, { name: "", phone: "" }])} className="mt-2 text-xs text-forest inline-flex items-center gap-1" data-testid="referral-manual-add-row"><Plus className="w-3 h-3" /> Another friend</button>
        {err && <div className="mt-2 text-sm text-clay" data-testid="referral-manual-error">{err}</div>}
        <p className="mt-3 text-[11px] text-sage">By saving you give us permission to keep these names and numbers for your invites only.</p>
        <button type="submit" disabled={busy} className="mt-3 w-full bg-forest text-white rounded-lg py-2.5 text-sm font-medium hover:bg-forest-deep disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="referral-manual-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save friends
        </button>
      </form>
    </div>
  );
}
